import { useMemo, useState } from "react";
import { PageRenderer } from "@/components/sections/PageRenderer";
import { BgVideo } from "@/components/common/BgVideo";
import { BG_VIDEOS, BG_POSTERS } from "@/config/media";
import { SectionTitle } from "@/components/common/SectionTitle";
import { ServiceCard } from "@/components/cards/ServiceCard";
import { AsyncState } from "@/components/common/Loader";
import { usePublicContent } from "@/lib/useContent";
import { useSite } from "@/context/SiteContext";
import { cn } from "@/lib/utils";

const FILTERS = ["All", "Athletes", "Adults", "Teams", "Online", "Volleyball", "Camps/Clinics"];

export default function Services() { return <PageRenderer pageKey="services" />; }

export const ServicesBody = () => {
  const { data, loading } = usePublicContent("services");
  const { settings } = useSite();
  const [active, setActive] = useState("All");

  const filtered = useMemo(() => {
    if (active === "All") return data;
    return data.filter((s) => Array.isArray(s.categories) && s.categories.includes(active));
  }, [data, active]);

  return (
    <div data-testid="services-page">
      {/* hero via PageRenderer */}

      <section id="services-programs" className="scroll-mt-24 bg-background py-16 sm:py-20">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6">
          <SectionTitle
            eyebrow="Choose Your Path"
            title="Programs Built For You"
            subtitle="Filter by who you are or what you need, then book or view packages in one tap."
          />

          {/* Filter tabs */}
          <div
            className="mt-8 flex flex-wrap gap-2"
            role="tablist"
            aria-label="Service filters"
            data-testid="service-filter-tabs"
          >
            {FILTERS.map((f) => {
              const isActive = active === f;
              return (
                <button
                  key={f}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActive(f)}
                  data-testid={`service-filter-${f.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  className={cn(
                    "rounded-full border px-4 py-2 font-condensed text-xs font-semibold uppercase tracking-[0.12em] transition-colors duration-200 active:scale-[0.98]",
                    isActive
                      ? "border-[hsl(var(--brand-red))] bg-brand text-white"
                      : "border-border bg-transparent text-muted-foreground hover:border-[hsl(var(--brand-red))] hover:text-foreground"
                  )}
                >
                  {f}
                </button>
              );
            })}
          </div>

          {/* Service grid */}
          <div className="mt-10">
            <AsyncState loading={loading} empty={filtered.length === 0} emptyTitle="No services in this category" emptyDescription="Try a different filter.">
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {filtered.map((s) => <ServiceCard key={s.id} item={s} />)}
              </div>
            </AsyncState>
          </div>

          {/* Closing CTA — larger video-backed window (B&W gym loop + red overlay) */}
          <div id="services-start" className="scroll-mt-24">
          <BgVideo
            videoUrl={BG_VIDEOS.gym}
            poster={BG_POSTERS.gym}
            redOpacity={42}
            testid="services-cta-window"
            className="mt-14 rounded-2xl"
          >
            <div className="px-6 py-20 text-center sm:px-10 sm:py-28">
              <h3 className="display-title text-4xl text-white sm:text-6xl">Not Sure Where To Start?</h3>
              <p className="mx-auto mt-4 max-w-xl text-base text-white/90 sm:text-lg">
                Book a free intro workout and evaluation. We'll match you to the right program.
              </p>
              <a
                href={settings.exercise_booking_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="external-exercisecom-link"
                className="mt-8 inline-flex items-center justify-center rounded-md bg-white px-8 py-4 text-sm font-semibold uppercase tracking-wide text-black transition-colors duration-200 hover:bg-white/90 active:scale-[0.98]"
              >
                Book Intro Workout
              </a>
            </div>
          </BgVideo>
          </div>
        </div>
      </section>
    </div>
  );
}
